import * as React from 'react';
import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import {
  selectAppSettings,
  selectAssetPopupSettings,
  selectAuthenticatedUser,
  selectGlobalServiceGroupFilter,
  selectGlobalSettingBool,
  selectglobalSettings,
  selectIsAuthenticated,
  selectPublicUrl,
} from 'app/slice/selectors';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AllowedSettings } from 'utils/globalSettings';

import { IAsset } from './IAsset';
import { IAssetRow } from './IAssetRow';
import { getRows } from './Rows';
import { Badge, MenuItem } from '@material-ui/core';
import { httpClient } from 'api/HttpClient';
import { translations } from 'locales/translations';
import { Button } from 'app/components/BasicButtons/Button';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { buildURL, toRootedURL } from 'utils/url-utils';
import { useAppSettingsSlice } from 'app/slice';
import {
  CoverProps,
  PageWrapper,
} from 'app/Layout/FrontendLayout/components/PageWrapper';
import { ActionRenderer } from 'app/Layout/FrontendLayout/components/PageWrapper/PageActions/ActionRender';
import { useTopAssetActionsDropDown } from './components/TopAssetActionsDropDown';
import { CalendarButton } from 'app/components/CalendarButton';
import { ServiceType } from 'enums/ServiceTypes';
import { CreateAnAssetRelatedEvent } from './CreateAnAssetRelatedEvent';
import {
  AvailabilityTypes,
  AvailabilityTypesUnion,
} from 'api/odata/generated/enums/AvailabilityTypes';
import { assertExhaustive } from 'utils/assertExhaustive';
import { IAssetDetails2Dto } from 'api/odata/generated/entities/IAssetDetails2Dto';
import { selectExpandedSidePanel } from 'app/Layout/FrontendLayout/slice/selectors';
import { AssetDetailsTabContent } from './components/AssetDetailsTabContent';
import { DetectIsMobile } from 'utils/mobileDetect';
import { AssetBookingRules } from './components/AssetBookingRules';
import {
  AssetTrainingSessions,
  GetTrainingSessionsTableCount,
} from './components/AssetTrainingSessions';
import {
  AssetArticlesTable,
  GetArticlesTableCount,
} from './components/AssetArcticlesTable';
import {
  AssetWorkOrders,
  GetWorkOrdersTableCount,
} from './components/AssetWorkOrders';
import {
  GetAlertsEnabled,
  GetReservationEnabled,
  GetUsageEnabled,
  addArrayItemByName,
  isBookable,
  moveArrayItem,
  fetchAssetBy,
  fetchAssetAdditionalData,
  moveArrayItemByName,
  isOnlineService,
  AssetIdentifier,
  AssetIdIdentifier,
  AssetServiceIdentifier,
  AssetInternalCodeIdentifier,
} from './components/Utils';
import { TabContent } from 'app/components/BasicTabs/TabContent';
import { BadgeCounter } from './components/BadgeCounter';
import { DropDown } from 'app/components/DropDown';
import { EndUsageConfirm, EndUsageData } from 'app/components/EndUsageConfirm';
import { AssetDetailsApi } from 'api/AssetDetailsApi';
import { AssetDetailsTabs } from './components/AssetDetailsTabs';
import { MismatchCore } from './components/MismatchCoreMessage';
import { ServiceGroupRegistrationStatus } from 'enums/ServiceGroupRegistrationStatus';
import {
  AssetDetailsAdditionalData,
  CountersForTabs,
  DefaultAttributes,
  DefaultRows,
  ExpandableRows,
} from './components/types';
import {
  AssetTypes,
  GetAssetTypeFrom,
  IsAssetFieldEnabled,
  IsAssetFieldIsPublic,
} from 'types/AppSettings';
import { AssetAttributes } from 'enums/AssetAttributes';
import useSidePanelState, {
  SidePanelCloseState,
  SidePanelOpenState,
} from 'app/hooks/useSidePanelOpen';
import { ReservationCreateButton } from 'app/components/ReservationCreateButton';
import { RenderPageType } from 'app/Layout/FrontendLayout/slice/type';
import { WorkOrderDetailsProps } from '../WorkOrders/WorkOrderDetailsPage/Details';
import { TrendingUpRounded } from '@material-ui/icons';
import { ReportConsumablesProps } from '../OtherServiceDetails/ReportConsumables';
import { useHistory, useLocation } from 'react-router-dom';
import { Messages } from 'app/components/Messages';
import { useLayoutSlice } from 'app/Layout/FrontendLayout/slice';
import { NotFoundPage } from '../NotFoundPage';

export function alertsEnabled(availabilityType: AvailabilityTypesUnion) {
  switch (availabilityType) {
    case 'FreelyBookable':
    case 'LoanableEquipment':
    case 'LectureRoom':
    case 'TrainingProgram':
    case 'NotAvailable':
    case 'AvailableNotBookable':
      return true;
    case 'ServiceGroup':
    case 'SuperGroup':
    case 'ServiceRelated':
    case 'RequestRelated':
      return false;
    default:
      assertExhaustive(availabilityType);
  }
}

const maxLeftTopButtonsLength = 2;
const countersForTabs = new CountersForTabs();

export interface AssetPopUpProps extends CoverProps {
  identifier: AssetIdentifier;
  onClose?: () => void;
  showAllCores?: boolean;
  useSidePanel: boolean;
  useRedirectToDash?: boolean;
  addFromCalendar?: boolean;
}

export const AssetPopUp = ({
  identifier,
  onClose,
  showAllCores,
  useSidePanel = true,
  useRedirectToDash,
  isCover,
  addFromCalendar = true,

  closeCover,
  ...props
}: AssetPopUpProps) => {
  const { t } = useTranslation();
  const globalSettings = useSelector(selectglobalSettings);
  const history = useHistory();
  const { actions: layoutActions } = useLayoutSlice();

  const [rows, setRows] = useState<IAssetRow[]>([]);
  const [selectColumns, setSelectColumns] = useState<string>('');
  const [expandColumns, setExpandColumns] = useState<string>('');
  const [data, setData] = useState<IAsset | undefined>();
  const [buttonsData, setButtonsData] = useState<IAsset | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const [idChanged, setIdChanged] = useState<boolean>(false);
  const [loadingAdditionalData, setLoadingAdditionalData] = useState<
    boolean | undefined
  >(undefined);
  const [
    loadingButtonsAdditionalData,
    setLoadingButtonsAdditionalData,
  ] = useState<boolean | undefined>(undefined);
  const [openEndUsage, setOpenEndUsage] = useState(false);
  const [endUsageProcessing, setEndUsageProcessing] = useState(false);
  const dispatch = useDispatch();
  const { actions } = useAppSettingsSlice();
  const publicUrl = useSelector(selectPublicUrl);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const {
    cover: assetCover,
    openPanel,
    closePanel,
    coverClosed,
    onCloseCover,
  } = useSidePanelState();
  const selectedServiceGroups = useSelector(selectGlobalServiceGroupFilter);
  const sidePanelExpanded = useSelector(selectExpandedSidePanel);
  const topServiceGroupsIsHidden = useSelector(state =>
    selectGlobalSettingBool(state, AllowedSettings.HideTopServiceGroupFilter),
  );
  const authenticatedUser = useSelector(selectAuthenticatedUser);
  const [counters, setCounters] = useState<CountersForTabs>(countersForTabs);
  const location = useLocation();
  const isMobile = DetectIsMobile();
  const appSettings = useSelector(selectAppSettings);
  const assetProps = useSelector(selectAssetPopupSettings);
  const shortView = React.useMemo(() => {
    return isMobile || (useSidePanel === true && !sidePanelExpanded) || isCover;
  }, [isCover, isMobile, sidePanelExpanded, useSidePanel]);
  const isAdmin = React.useMemo(() => {
    if (data === undefined) {
      return false;
    }
    const serviceGroupId = data?.ServiceGroup?.Id ?? undefined;
    const serviceId = data?.ServiceId ?? undefined;
    const serviceTypeId =
      data?.ServiceType === null
        ? undefined
        : ServiceType[data.ServiceType] ?? undefined;
    const result =
      authenticatedUser?.HasAdminGroupOnlyPermissions(serviceGroupId) ||
      authenticatedUser?.HasAdminServiceOnlyPermissions(
        serviceTypeId !== undefined ? serviceTypeId : 1,
        serviceId,
      );
    return result ?? false;
  }, [authenticatedUser, data]);
  const isDashboardCore = React.useMemo(() => {
    var path = location.pathname;
    return path.toLowerCase() === '/dashboards/core/' + data?.ServiceGroup?.Id;
  }, [data?.ServiceGroup?.Id, location.pathname]);
  const assetFieldEnabled = React.useCallback(
    (
      AssetField: AssetAttributes,
      AssetAvailabilityType?: AssetTypes,
    ): boolean => {
      if (isAuthenticated) {
        return IsAssetFieldEnabled(
          appSettings,
          AssetField,
          AssetAvailabilityType,
        );
      } else {
        return (
          IsAssetFieldEnabled(appSettings, AssetField, AssetAvailabilityType) &&
          IsAssetFieldIsPublic(appSettings, AssetField, AssetAvailabilityType)
        );
      }
    },
    [appSettings, isAuthenticated],
  );
  const updateCounters = useCallback(
    (name: string, count: number) => {
      const tempCounters = Object.assign({}, counters);
      tempCounters[name].Count = count;
      setCounters(tempCounters);
    },
    [counters],
  );
  const updateCountersEnable = useCallback(
    (name: string, enabled: boolean) => {
      const tempCounters = counters;
      tempCounters[name].Enabled = enabled;
      setCounters(tempCounters);
    },
    [counters],
  );
  const dict: Record<string, IAssetRow> = useMemo(() => {
    return getRows(
      t,
      globalSettings,
      openPanel,
      publicUrl,
      isCover,
      useSidePanel,
      isAdmin,
      shortView,
      isAuthenticated,
    ).reduce((res, current) => {
      res[current.Id] = current;
      return res;
    }, {});
  }, [
    t,
    globalSettings,
    openPanel,
    publicUrl,
    isCover,
    useSidePanel,
    isAdmin,
    shortView,
    isAuthenticated,
  ]);

  const mismatchCore = useMemo(() => {
    return MismatchCore(
      data,
      isAuthenticated,
      topServiceGroupsIsHidden,
      selectedServiceGroups,
    );
  }, [data, isAuthenticated, selectedServiceGroups, topServiceGroupsIsHidden]);
  const loadAdditionalData = useCallback(
    async (data: IAssetDetails2Dto) => {
      const res = Object.assign({}, data);
      if (!!res) {
        setLoadingAdditionalData(true);
        try {
          const ad:
            | AssetDetailsAdditionalData
            | undefined = await fetchAssetAdditionalData({
            Id: res.Id,
            typeId: AvailabilityTypes[res.AvailabilityType.Id] as number,
            isAuth: isAuthenticated,
            buttonsData: false,
          });
          if (!!ad) {
            res.ApprovalRequired = ad.ApprovalRequired;
            res.Status = ad.Status;
            res.StatusId = ad.StatusId;
            res.AssetAssemblyStructure = ad.AssemblyStructure;
            res.HideTrainingSession = ad.HideTrainingSession;
            res.Range = ad.Range;
            res.ForceTutoring = ad.ForceTutoring;
            res.Allowed_ByTraining = ad.Allowed_ByTraining;
            res.EndUsageIds = ad.EndUsageIds;
            if (res.ServiceGroup !== undefined && res.ServiceGroup !== null) {
              res.ServiceGroup.CanRequestAccess = ad.CanRequestAccess;
              res.ServiceGroup.ApprovalStatusId = ad.ApprovalStatusId;
            }
            res.CanBeCreatedFromLinkOnly = ad.CanBeCreatedFromLinkOnly;
            res.NotAllowReservations = ad.NotAllowReservations;
            res.ReservationEnabled = GetReservationEnabled(
              AvailabilityTypes[res.AvailabilityType.Id],
              res.Active,
              !!res.NotAllowReservations,
            );
            setData(res);
          }
        } finally {
          setLoadingAdditionalData(false);
        }
      }
    },
    [isAuthenticated],
  );
  const loadButtonsAdditionalData = useCallback(
    async (data: IAssetDetails2Dto) => {
      const res = Object.assign({}, buttonsData);
      if (!!data) {
        setLoadingButtonsAdditionalData(true);
        try {
          const ad:
            | AssetDetailsAdditionalData
            | undefined = await fetchAssetAdditionalData({
            Id: data.Id,
            typeId: AvailabilityTypes[data.AvailabilityType.Id] as number,
            isAuth: isAuthenticated,
            buttonsData: true,
          });
          if (!!ad) {
            res.AutoMode = ad.AutoMode;
            res.UsageEnabled = GetUsageEnabled(
              AvailabilityTypes[data.AvailabilityType.Id],
              data.Active,
              res.AutoMode,
            );
            res.UsageId = ad.UsageId;
            res.CanStartUsage = ad.CanStartUsage;
            res.CanEndUsage = ad.CanEndUsage;
            res.Alerts = ad.Alerts;
            res.PlanedEventsEnabled = ad.PlanedEventsEnabled;
            res.AlertInfo = ad.AlertInfo;
            res.NotifyExists = ad.NotifyExists;
            res.NotAllowReservations = ad.NotAllowReservations;
            res.AssemblyId = ad.AssemblyId;
            res.ReservationEnabled = GetReservationEnabled(
              AvailabilityTypes[data.AvailabilityType.Id],
              data.Active,
              !!res.NotAllowReservations,
            );
            res.ShowFeed = ad.ShowFeed;
            setButtonsData(res);
          }
        } finally {
          setLoadingButtonsAdditionalData(false);
        }
      }
    },
    [buttonsData, isAuthenticated],
  );

  const loadCounters = useCallback(
    (data: IAssetDetails2Dto) => {
      if (data.Id !== undefined) {
        if (counters.TrainingSessions.Enabled) {
          GetTrainingSessionsTableCount(
            data.Id,
            'TrainingSessions',
            updateCounters,
            data.ServiceGroup.Id,
            data.AvailabilityType.Id,
          );
        }
        GetWorkOrdersTableCount(
          data.Id,
          false,
          'WorkOrders',
          updateCounters,
          data.ServiceGroup.Id,
          data.AvailabilityType.Id,
        );
        GetWorkOrdersTableCount(
          data.Id,
          true,
          'Calibrations',
          updateCounters,
          data.ServiceGroup.Id,
          data.AvailabilityType.Id,
        );
        GetArticlesTableCount(
          data.Id,
          data.ServiceGroup?.Id,
          'Publications',
          updateCounters,
          data.AvailabilityType.Id,
        );
      }
    },
    [counters.TrainingSessions.Enabled, updateCounters],
  );

  const enabledAssetProps = React.useMemo(() => {
    const id = data?.AvailabilityType?.Id;
    if (id !== undefined) {
      const res = assetProps.filter(a =>
        assetFieldEnabled(
          AssetAttributes[a.Id],
          GetAssetTypeFrom(AvailabilityTypes[id]),
        ),
      );
      return res;
    } else {
      return assetProps;
    }
  }, [assetFieldEnabled, assetProps, data?.AvailabilityType?.Id]);
  const columnNamesFilter = React.useCallback((r: IAssetRow) => {
    return (
      r.State === undefined &&
      [
        'AssetAssemblyStructure',
        'Status',
        'Division',
        'AssetCatGroup',
        'Range',
      ].includes(r.PropName ?? r.Id) === false &&
      r.Id !== 'ForceTutoring'
    );
  }, []);
  const setRequestParams = React.useCallback(
    (rows: IAssetRow[]) => {
      if (rows.length > 0) {
        let columns = [
          ...rows
            .filter(r => columnNamesFilter(r))
            .map(v => v.PropName ?? v.Id),
          ...DefaultAttributes,
        ];
        setSelectColumns('Active,' + columns.join(','));
        setExpandColumns(
          columns
            .filter(column => ExpandableRows.includes(column))
            //.concat('ServiceGroup')
            .join(','),
        );
      }
    },
    [columnNamesFilter],
  );
  useEffect(() => {
    if (!!data) {
      if ((identifier as AssetIdIdentifier).id !== undefined) {
        if (data.Id !== (identifier as AssetIdIdentifier).id) {
          setIdChanged(true);
        }
      } else if (
        (identifier as AssetServiceIdentifier).serviceId !== undefined &&
        (identifier as AssetServiceIdentifier).serviceType !== undefined
      ) {
        if (
          data.ServiceId !== (identifier as AssetServiceIdentifier).serviceId
        ) {
          setIdChanged(true);
        }
      } else if (
        (identifier as AssetInternalCodeIdentifier).internalCode !== undefined
      ) {
        if (
          data.InternalCode !==
          (identifier as AssetInternalCodeIdentifier).internalCode
        ) {
          setIdChanged(true);
        }
      }
    }
  }, [data, identifier]);
  useEffect(() => {
    moveArrayItem(enabledAssetProps, 'ImageName', 0);
    addArrayItemByName(
      enabledAssetProps,
      'AssetAssemblyStructure',
      'ImageName',
    );
    moveArrayItemByName(enabledAssetProps, 'Status', 'ServiceGroup');
    const rs = [
      ...DefaultRows.map(r => dict[r]),
      ...enabledAssetProps.flatMap(assetProp => {
        let val = dict[assetProp.Id];
        if (!!val) {
          val.Name = assetProp.DisplayName ?? val.Name;
          if (val.dependencies && !!val.dependencies.length) {
            return [val, ...val.dependencies.map(v => dict[v])];
          }
        }
        return val;
      }),
    ].filter(v => !!v);
    setRequestParams(rs);
    setRows(rs);
  }, [assetProps, dict, enabledAssetProps, setRequestParams]);
  useEffect(() => {
    if (rows.length === 0 || (data !== undefined && idChanged === false))
      return;
    setLoading(true);
    let active = true;
    const cancelToken = httpClient.cancelToken.source();
    async function fetchData() {
      try {
        const params = {
          $select: selectColumns,
          $expand: expandColumns,
        };

        const res = await fetchAssetBy({
          id: identifier,
          params,
          isAuth: isAuthenticated,
        });

        if (active && res !== undefined) {
          res.Division = res.ServiceGroup?.DivisionName;
          res.AssetCatGroup =
            res.AssetCat !== null && !!res.AssetCat
              ? res.AssetCat.AssetCatGroupName
              : null;
          res.Status = ' ';
          res.AlertsEnabled = isAuthenticated
            ? GetAlertsEnabled(
                AvailabilityTypes[res.AvailabilityType.Id],
                res.Active,
              )
            : false;
          setData(res);
          loadButtonsAdditionalData(res);
          loadAdditionalData(res);
          if (isAuthenticated) {
            loadCounters(res);
          }
        }
        setLoading(false);
        setIdChanged(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }

    fetchData();

    return () => {
      active = false;
      setLoading(false);
      cancelToken.cancel('cancelled');
    };
  }, [
    data,
    expandColumns,
    idChanged,
    identifier,
    isAuthenticated,
    loadAdditionalData,
    loadButtonsAdditionalData,
    loadCounters,
    rows.length,
    selectColumns,
  ]);

  const handleEndUsage = React.useCallback(
    (usageData: EndUsageData) => {
      if (buttonsData?.UsageId) {
        setEndUsageProcessing(true);
        AssetDetailsApi.endUsage(buttonsData?.UsageId, usageData)
          .then(response => {
            setEndUsageProcessing(false);

            if (response.ErrorMessages.length > 0) {
              response.ErrorMessages.forEach(errmsg =>
                dispatch(
                  actions.addNotification({
                    variant: 'error',
                    message: errmsg,
                  }),
                ),
              );
            } else {
              setOpenEndUsage(false);
              if (data !== undefined) {
                loadButtonsAdditionalData(data);
              }
              if (response.SuccessMessages.length > 0) {
                response.SuccessMessages.forEach(msg =>
                  dispatch(
                    actions.addNotification({
                      variant: 'success',
                      message: msg,
                    }),
                  ),
                );
              }
              if (response.WarningMessages.length > 0) {
                response.WarningMessages.forEach(wrnmsg =>
                  dispatch(
                    actions.addNotification({
                      variant: 'warning',
                      message: wrnmsg,
                    }),
                  ),
                );
              }
            }
          })
          .catch(e => console.error(e))
          .finally(() => setEndUsageProcessing(false));
      }
    },
    [actions, buttonsData?.UsageId, data, dispatch, loadButtonsAdditionalData],
  );

  const getTopAssetActionsDropDown = useTopAssetActionsDropDown({
    isMismatchCore: mismatchCore,
    isAdmin: isAdmin,
  });

  const topActions = useMemo(() => {
    let buttonActions = [] as ActionRenderer[];
    if (
      data !== undefined &&
      buttonsData !== undefined &&
      data.AvailabilityType !== undefined &&
      loading === false &&
      loadingButtonsAdditionalData === false
    ) {
      const bookable = isBookable(data.AvailabilityType.Id);
      const addWorkOrder = () => {
        if (alertsEnabled(data.AvailabilityType.Id) && isAuthenticated) {
          buttonActions.push(() => (
            <CreateAnAssetRelatedEvent
              assetId={data.Id}
              mismatchCore={mismatchCore}
              useSidePanel={useSidePanel}
              openPanel={openPanel}
            />
          ));
        }
      };
      const addReservation = () => {
        if (!buttonsData.AssemblyId && buttonsData.ReservationEnabled) {
          buttonActions.push(() => (
            <ReservationCreateButton
              size="small"
              color="primary"
              startIcon={<Icon icon="plus" />}
              variant={bookable ? 'main' : 'white'}
              title={t(translations.PlaceAReservation)}
              disabled={mismatchCore}
              instruments={data.ServiceId === null ? [] : [data.ServiceId]}
              useSidePanel={useSidePanel}
              openPanelOrCover={openPanel}
            >
              {t(translations.Reservation)}
            </ReservationCreateButton>
          ));
        }
      };
      const addServiceRequest = () => {
        if (
          data.ServiceType === 'Sample' &&
          data.CanBeCreatedFromLinkOnly === false
        ) {
          buttonActions.push(() => (
            <Button
              key="service-request"
              size="small"
              onClick={() => {
                history.push(
                  '/ServiceRequests?OfflineServiceTypeId=' +
                    data.ServiceId +
                    '&Active=true',
                );
              }}
              title={t(translations.SampleRequests)}
              startIcon={<Icon icon={'history'} />}
              disabled={mismatchCore}
            >
              {t(translations.SampleRequests)}
            </Button>
          ));
        }
      };
      const addReservationFromCalendar = () => {
        if (!buttonsData.AssemblyId && buttonsData.ReservationEnabled) {
          buttonActions.push(() => (
            <CalendarButton
              size="small"
              color="primary"
              variant={bookable ? 'main' : 'white'}
              startIcon={<Icon icon="plus" />}
              title={t(translations.PlaceAReservation)}
              equipments={data.ServiceId === null ? [] : [data.ServiceId]}
              disabled={mismatchCore}
              openToBook={true}
              target={isAuthenticated ? '_self' : '_blank'}
            >
              {t(translations.Reservation)}
            </CalendarButton>
          ));
        }
      };
      const addUsage = () => {
        if (!!buttonsData.UsageEnabled) {
          if (!!buttonsData.CanStartUsage && !buttonsData.AssemblyId) {
            buttonActions.push(() => (
              <Button
                size="small"
                variant="white"
                startIcon={<Icon icon="play" />}
                href={toRootedURL(`/Usage.aspx?id=${data.ServiceId}`)}
                target={'_blank'}
                title={t(translations.StartUsage)}
                disabled={mismatchCore}
              >
                {t(translations.Use)}
              </Button>
            ));
          }
          if (!!buttonsData.CanEndUsage && !!buttonsData.UsageId) {
            buttonActions.push(() => (
              <Button
                size="small"
                variant="white"
                startIcon={<Icon icon="pause" />}
                title={t(translations.StopUsage)}
                onClick={() => setOpenEndUsage(true)}
                disabled={mismatchCore}
              >
                {t(translations.Stop)}
              </Button>
            ));
          }
        }
      };
      const addTakeDown = () => {
        if (
          isBookable(data.AvailabilityType?.Id) &&
          buttonsData.AlertInfo !== undefined &&
          !!buttonsData.PlanedEventsEnabled &&
          !!buttonsData.AlertInfo.show &&
          isAuthenticated
        ) {
          if (!buttonsData.AlertInfo.hasDownActive) {
            if (
              !!buttonsData.AlertInfo.isAdmin &&
              !!buttonsData.AlertInfo.assetId
            ) {
              buttonActions.push(() => (
                <DropDown
                  size="small"
                  color="primary"
                  variant="white"
                  disabled={mismatchCore}
                  title={t(translations.HasNoAlertsAdmin_info)}
                  menuChildren={() => {
                    let res = [
                      <MenuItem key={'title for this menu'} disabled>
                        {t(translations.PleaseSelectAnEventType)}
                      </MenuItem>,
                    ];
                    if (buttonsData.Alerts !== undefined) {
                      res.push(
                        ...buttonsData.Alerts.map(a => (
                          <MenuItem
                            onClick={() =>
                              openPanel({
                                renderPageType: RenderPageType.WorkOrderDetails,
                                renderPageProps: {
                                  queryParams: {
                                    aType: a.Id.toString(),
                                    assetId:
                                      !!buttonsData.AlertInfo &&
                                      !!buttonsData.AlertInfo.assetId
                                        ? buttonsData.AlertInfo.assetId.toString()
                                        : undefined,
                                    down: true.toString(),

                                    offH: true.toString(),
                                  },
                                  useSidePanel: true,
                                } as WorkOrderDetailsProps,
                                expanded: false,
                                useSidePanel: useSidePanel,
                                isCover: useSidePanel,
                              })
                            }
                            key={a.Id + a.Name + 'key'}
                          >
                            {a.Name}
                          </MenuItem>
                        )),
                      );
                    }
                    return res;
                  }}
                >
                  <Icon icon="power-off" color="danger" />
                </DropDown>
              ));
            }
          } else if (
            !!buttonsData.AlertInfo.noAlerts ||
            !!buttonsData.AlertInfo.hasActive
          ) {
            if (
              !!buttonsData.AlertInfo.isAdmin &&
              !!buttonsData.AlertInfo.alertId
            ) {
              buttonActions.push(() => (
                <Button
                  size="small"
                  variant="white"
                  disabled={mismatchCore}
                  title={t(translations.HasActiveDownAlertsAdmin_info)}
                  onClick={() =>
                    openPanel({
                      renderPageType: RenderPageType.WorkOrderDetails,
                      renderPageProps: {
                        queryParams: {
                          id: (buttonsData.AlertInfo?.alertId ?? -1).toString(),
                          Up: TrendingUpRounded.toString(),
                        },
                        useSidePanel: true,
                      } as WorkOrderDetailsProps,
                      expanded: false,
                      useSidePanel: useSidePanel,
                      isCover: useSidePanel,
                    } as SidePanelOpenState)
                  }
                >
                  <Icon icon="plug" color="success" />
                </Button>
              ));
            }
          }
        }
      };

      const addConsumable = () => {
        if (data.ServiceType === 'Offline' && isAuthenticated) {
          buttonActions.push(() => (
            <Button
              size="small"
              variant="main"
              disabled={mismatchCore}
              startIcon={<Icon icon="plus" />}
              title={t(translations.ReportAConsumable)}
              onClick={() => {
                let params = {
                  useSidePanel: true,
                  queryParams: {
                    stid: data.ServiceId?.toString(),
                    user: authenticatedUser?.Id,
                  },
                } as ReportConsumablesProps;
                openPanel({
                  renderPageType: RenderPageType.ReportConsumables,
                  renderPageProps: params,
                  expanded: false,
                  useSidePanel: useSidePanel,
                  isCover: useSidePanel,
                } as SidePanelOpenState);
              }}
              //href={toRootedURL(`/reportConsumables?stid=${data.ServiceId}`)}
            >
              {t(translations.LogConsumables)}
            </Button>
          ));
        }
      };

      const addCoreBoard = () => {
        if (!isDashboardCore) {
          buttonActions.push(() => (
            <Button
              size="small"
              variant="white"
              disabled={mismatchCore}
              title={t(translations.SwitchToCoreOpenItsBoard)}
              onClick={() => {
                dispatch(
                  actions.saveGlobalServiceGroupFilter([
                    {
                      Id: data.ServiceGroup?.Id,
                      Name: data.ServiceGroup?.Name,
                    },
                  ]),
                );
                dispatch(layoutActions.resetSidePanel());
                history.push('/dashboards/core/' + data.ServiceGroup?.Id ?? '');
              }}
            >
              {t(translations.CoreBoard)}
            </Button>
          ));
        }
      };
      const addRequestAccess = () => {
        if (!isAdmin && isAuthenticated) {
          const approvalStatus =
            data.ServiceGroup?.ApprovalStatusId ===
            ServiceGroupRegistrationStatus.Approved;
          if (data.ServiceGroup?.CanRequestAccess && !approvalStatus) {
            buttonActions.push(() => (
              <Button
                size="small"
                variant="main"
                onClick={() => {
                  window.open(
                    toRootedURL(
                      `/ServiceGroupRegister.aspx?sgid=${data.ServiceGroup?.Id}`,
                    ),
                    '_blank',
                  );
                }}
              >
                {t(translations.RequestAccess)}
              </Button>
            ));
          } else {
            buttonActions.push(() => (
              <Badge>
                {t(
                  translations[
                    ServiceGroupRegistrationStatus[
                      data.ServiceGroup?.ApprovalStatusId ?? 0
                    ]
                  ],
                )}
              </Badge>
            ));
          }
        }
      };
      if (
        data.ServiceType === 'Sample' &&
        data.CanBeCreatedFromLinkOnly === false
      ) {
        addServiceRequest();
      }
      if (bookable) {
        if (addFromCalendar) {
          addReservationFromCalendar();
        } else {
          addReservation();
        }

        addUsage();
        addWorkOrder();
        addTakeDown();
      } else {
        addWorkOrder();
        addTakeDown();
        addUsage();
        addReservation();
        addConsumable();
      }
      addCoreBoard();
      addRequestAccess();
      if (isAuthenticated) {
        const assetActionsDropDown = getTopAssetActionsDropDown({
          data,
          buttonsData,
        });

        buttonActions.push(...assetActionsDropDown.map(item => () => item));
      }
    }
    return buttonActions;
  }, [
    actions,
    addFromCalendar,
    authenticatedUser?.Id,
    buttonsData,
    data,
    dispatch,
    getTopAssetActionsDropDown,
    history,
    isAdmin,
    isAuthenticated,
    isDashboardCore,
    layoutActions,
    loading,
    loadingButtonsAdditionalData,
    mismatchCore,
    openPanel,
    t,
    useSidePanel,
  ]);

  const tabs = useMemo(() => {
    if (data !== undefined) {
      const res: TabContent[] = [];
      res.push({
        Name: t(translations.AssetDetailsTab_Details),
        Id: 'details',
        Content: (
          <AssetDetailsTabContent
            rows={rows}
            data={data}
            shortView={shortView}
            loadingAdditionalData={loadingAdditionalData}
            isAdmin={isAdmin === true}
            isCover={isCover}
            commentsForStaff={!!data.CommentsAreStaffonly}
          />
        ),
      });
      if (isBookable(data.AvailabilityType?.Id) && isAuthenticated) {
        res.push({
          Name: t(translations.BookingRules),
          Id: 'BookingRules',
          Content: (
            <AssetBookingRules
              serviceId={data.ServiceId ?? undefined}
              serviceGroupId={data.ServiceGroup?.Id ?? undefined}
              useShortView={shortView}
              useSidePanel={useSidePanel}
            />
          ),
        });
      }
      if (isBookable(data.AvailabilityType?.Id) && isAuthenticated) {
        if (
          data.AvailabilityType?.Id !== 'LectureRoom' ||
          (!data.HideTrainingSession && data.Active)
        ) {
          const serviceType =
            data.ServiceType !== null
              ? ServiceType[data.ServiceType]
              : undefined;
          res.push({
            Name: t(translations.TrainingSessions),
            Id: 'trainingSessions',
            Content: (
              <AssetTrainingSessions
                assetId={data.Id}
                serviceId={data.ServiceId}
                serviceTypeId={serviceType}
                isMobile={shortView}
                serviceGroupId={data.ServiceGroup?.Id}
                availabilityType={data.AvailabilityType?.Id}
                openPanel={openPanel}
                useSidePanel={useSidePanel}
              />
            ),
            endIcon: <BadgeCounter count={counters.TrainingSessions.Count} />,
          });
          updateCountersEnable('TrainingSessions', true);
        } else {
          updateCountersEnable('TrainingSessions', false);
        }
      }
      if (isAuthenticated) {
        res.push({
          Name: t(translations.AssetDetailsTab_WorkOrders),
          Id: 'workOrders',
          Content: (
            <AssetWorkOrders
              assetId={data.Id}
              assetName={data.Name}
              isMobile={shortView}
              allowToAddNew={isOnlineService(data.AvailabilityType?.Id)}
              openPanel={openPanel}
              useSidePanel={useSidePanel}
              serviceGroupId={data.ServiceGroup?.Id}
              availabilityType={data.AvailabilityType?.Id}
            />
          ),
          endIcon: <BadgeCounter count={counters.WorkOrders.Count} />,
        });
      }
      if (isAuthenticated) {
        res.push({
          Name: t(translations.Calibrations),
          Id: 'calibrations',
          Content: (
            <AssetWorkOrders
              assetId={data.Id}
              assetName={data.Name}
              calibration={true}
              isMobile={shortView}
              openPanel={openPanel}
              useSidePanel={useSidePanel}
              allowToAddNew={isOnlineService(data.AvailabilityType?.Id)}
              serviceGroupId={data.ServiceGroup?.Id}
              availabilityType={data.AvailabilityType?.Id}
            />
          ),
          endIcon: <BadgeCounter count={counters.Calibrations.Count} />,
        });
      }
      if (isAuthenticated) {
        res.push({
          Name: t(translations.Publications),
          Id: 'publications',
          Content: (
            <AssetArticlesTable
              assetId={data.Id}
              serviceGroupId={data.ServiceGroup?.Id}
              isMobile={shortView}
              openPanel={openPanel}
              availabilityType={data.AvailabilityType?.Id}
              useSidePanel={useSidePanel}
            />
          ),
          endIcon: <BadgeCounter count={counters.Publications.Count} />,
        });
      }
      /*res.push({
          Name:
            t(translations.AssetDetailsTab_MyReservations) +
            ` (${counters.Reservations.Count})`,
          Id: 'my_reservations',
          Content: <>reservations</>,
        });*/
      /*res.push({
          Name: t(translations.AssetDetailsTab_Schedule),
          Id: 'schedule',
          Content: <>schedule</>,
        });*/
      return res;
    } else return [];
  }, [
    counters.Calibrations.Count,
    counters.Publications.Count,
    counters.TrainingSessions.Count,
    counters.WorkOrders.Count,
    data,
    isAdmin,
    isAuthenticated,
    isCover,
    loadingAdditionalData,
    openPanel,
    rows,
    shortView,
    t,
    updateCountersEnable,
    useSidePanel,
  ]);

  const handleCloselClick = React.useCallback(() => {
    closePanel({
      isCover: isCover,
      useSidePanel: useSidePanel,
      onClose: () => {
        !!onClose && onClose();
      },
    } as SidePanelCloseState);
  }, [closePanel, isCover, onClose, useSidePanel]);

  // more direct loading/fetch state indicator will be more appropriate here to determine that fetch operations are finished and no asset details were found
  const dataFound = data && tabs !== undefined && tabs.length > 0;
  if (!loading && !dataFound && data === undefined) {
    return <NotFoundPage />;
  }
  return (
    <PageWrapper
      id="asset_details"
      useSidePanel={useSidePanel ?? false}
      hideMenu={!isAuthenticated}
      closable={useSidePanel === true}
      loading={loading || rows.length === 0}
      pageLink={
        data === undefined
          ? undefined
          : buildURL(`${publicUrl}Assets/Details`, { id: data?.Id })
      }
      toPageLink={
        data === undefined ? undefined : `Assets/Details?id=${data?.Id}`
      }
      pageName={t(translations.Instrument)}
      titlePage={data?.Name ?? undefined}
      titleTooltip={data?.Name + t(translations.ShowAssetFull)}
      children={
        <Fragment>
          {dataFound ? (
            <React.Fragment>
              <AssetDetailsTabs
                tabs={tabs}
                isMobile={isMobile}
                shortView={shortView ?? false}
              />
              {isAuthenticated && buttonsData?.UsageId && (
                <React.Fragment>
                  <EndUsageConfirm
                    ids={data.EndUsageIds}
                    usageId={buttonsData?.UsageId}
                    isopen={openEndUsage}
                    usageProcessing={endUsageProcessing}
                    onClose={() => setOpenEndUsage(false)}
                    onConfirm={handleEndUsage}
                  />
                </React.Fragment>
              )}
              <Messages pageName="assetDetails" />
            </React.Fragment>
          ) : (
            <>{t(translations.err_Equipment_NotFound)}</>
          )}
        </Fragment>
      }
      closeSidePanel={handleCloselClick}
      rightTopActions={!shortView ? topActions : undefined}
      rightActionsLoading={!shortView && loadingButtonsAdditionalData}
      leftTopActions={shortView ? topActions : undefined}
      leftActionsLoading={shortView && loadingButtonsAdditionalData}
      maxLeftTopButtonsLength={
        data?.ReservationEnabled ? maxLeftTopButtonsLength : 1
      }
      rightTopActionsLength={data?.ReservationEnabled ? 2 : 1}
      isCover={isCover}
      closeCover={!isCover ? onCloseCover : closeCover}
      cover={assetCover}
      coverClosed={coverClosed}
    />
  );
};
